import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

// Initialize Stripe with publishable key from environment
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!);



const CheckoutForm: React.FC = () => {
  const [searchParams] = useSearchParams();
  const plan = searchParams.get('plan') || 'trial';
  const { user, userProfile, signUp, updateUserProfile } = useAuth();
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  
  const [formData, setFormData] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    name: '',
    email: userProfile?.email || '',
    password: '',
    confirmPassword: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US',
    studyState: ''
  });
  const [loading, setLoading] = useState(false);
  const submitLock = useRef(false);
  const [error, setError] = useState('');
  const [isSignup, setIsSignup] = useState(!user);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  const plans = {
    trial: {
      name: 'Free Trial',
      price: 0,
      period: 'trial',
      savings: '3 days free',
      features: [
        '500+ State-Specific Flashcards',
        'State-Specific Practice Test Questions',
        'Swipe-to-Study Interface',
        'Bookmark & Save Questions',
        'Real-Time Progress Tracking',
        'Study Statistics & Analytics',
        'Dark Mode & Mobile Access',
        'State-Specific Content (FL/GA/CA/TX/NY/NJ)',
        'Detailed Explanations',
        'Session Resume Functionality'
      ]
    },
    annual: {
      name: 'Annual Plan',
      price: 99.00,
      period: 'year',
      savings: 'Best Value',
      features: [
        '500+ State-Specific Flashcards',
        'State-Specific Practice Test Questions',
        'Swipe-to-Study Interface',
        'Bookmark & Save Questions',
        'Real-Time Progress Tracking',
        'Study Statistics & Analytics',
        'Dark Mode & Mobile Access',
        'State-Specific Content (FL/GA/CA/TX/NY/NJ)',
        'Detailed Explanations',
        'Session Resume Functionality',
        'Category-Based Study Sessions',
        'Practice Test Hints & Explanations',
        'Unlimited Study Sessions',
        'Priority Support',
        'Early Access to New States'
      ]
    }
  };

  const selectedPlan = plans[plan as keyof typeof plans] || plans.trial;

  useEffect(() => {
    // Update signup state when user changes
    setIsSignup(!user);
  }, [user]);

  // Ensure page loads at top
  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitLock.current) return;
    submitLock.current = true;
    setLoading(true);
    setError('');

    try {
      // Validate study state selection
      if (!formData.studyState) {
        setError('Please select your study state before proceeding');
        setLoading(false);
        return;
      }

      // Validate passwords if signing up
      if (isSignup && formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        setLoading(false);
        return;
      }

      // Step 1: Get Stripe elements and create payment method FIRST
      if (!stripe || !elements) {
        setError('Stripe has not loaded yet. Please try again.');
        setLoading(false);
        return;
      }

      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        setError('Card element not found. Please enter your card details.');
        setLoading(false);
        return;
      }

      // Create payment method from the actual card element
      const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: formData.name,
          email: formData.email,
          address: {
            line1: formData.addressLine1,
            line2: formData.addressLine2,
            city: formData.city,
            state: formData.state,
            postal_code: formData.zipCode,
            country: formData.country,
          },
        },
      });

      if (paymentMethodError) {
        setError(paymentMethodError.message || 'Payment method creation failed.');
        setLoading(false);
        return;
      }

      // Step 3: Process payment through backend
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3002'}/create-payment-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: selectedPlan.price,
          currency: 'usd',
          paymentMethodId: paymentMethod.id,
          customerEmail: formData.email,
          customerName: formData.name,
          planType: selectedPlan.period,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.userMessage || errorData.details || 'Payment failed');
      }

      const result = await response.json();

      
      // Step 3: Create Supabase user account AFTER successful payment
      if (isSignup) {
        try {

          await signUp(formData.email, formData.password, formData.name);

        } catch (signupError) {
          console.error('Error creating user account:', signupError);
          setError('Payment successful but failed to create user account. Please contact support.');
          setLoading(false);
          return;
        }
      }
      
      // Step 4: Update user profile with subscription info
      // Save state to localStorage as backup
      localStorage.setItem('selectedState', formData.studyState);
      
      if (user && userProfile) {
        try {
          console.log('Updating user profile with state:', formData.studyState);
          await updateUserProfile({
            subscription_type: selectedPlan.period === 'trial' ? 'trial' : 'paid',
            subscription_plan_type: selectedPlan.period === 'trial' ? 'annual' : selectedPlan.period === 'year' ? 'annual' : 'monthly',
            state: formData.studyState
          });
          console.log('User profile updated successfully with state:', formData.studyState);
        } catch (profileError) {
          console.error('Error updating user profile:', profileError);
          // Try again after a short delay
          setTimeout(async () => {
            try {
              console.log('Retrying profile update...');
              await updateUserProfile({
                subscription_type: selectedPlan.period === 'trial' ? 'trial' : 'paid',
                subscription_plan_type: selectedPlan.period === 'trial' ? 'annual' : selectedPlan.period === 'year' ? 'annual' : 'monthly',
                state: formData.studyState
              });
              console.log('Profile update retry successful');
            } catch (retryError) {
              console.error('Profile update retry failed:', retryError);
            }
          }, 2000);
          
          // Show warning to user but don't fail the whole process
          setError(`Profile update had issues but your subscription was created. State selection saved locally. If you don't see your state on the dashboard, please contact support.`);
        }
      } else {
        console.warn('Cannot update profile - user or userProfile not available');
        setError('Unable to save your state selection immediately. State saved locally - please contact support if not reflected on dashboard.');
      }
      
      if (result.planType === 'trial') {
        // Trial subscription created successfully

        localStorage.setItem('trialSubscriptionId', result.subscriptionId);
        localStorage.setItem('trialEndDate', result.trialEnd);
      }
      
      // Step 5: Redirect to dashboard after successful payment/trial setup
      navigate('/dashboard');
    } catch (error) {
      console.error('Payment error:', error);
      setError('Payment failed. Please try again.');
    } finally {
      submitLock.current = false;
      setLoading(false);
    }
  };

 



  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
          <div className="flex justify-between items-center py-3 sm:py-4">
            {/* Clickable Logo - Navigate based on auth status */}
            <button 
              onClick={() => {
                if (user) {
                  navigate('/dashboard');
                } else {
                  navigate('/');
                }
              }}
              className="flex items-center space-x-2 sm:space-x-3 hover:opacity-80 transition-opacity cursor-pointer"
            >
              <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xs sm:text-sm">R</span>
              </div>
              <span className="text-lg sm:text-xl font-bold text-gray-900">RealEstart.io</span>
            </button>
            
            {/* Security Badges - Responsive layout */}
            <div className="flex flex-col sm:flex-row items-end sm:items-center space-y-1 sm:space-y-0 sm:space-x-4">
              <span className="text-xs sm:text-sm text-gray-500 text-right sm:text-left">Secure Checkout</span>
              <div className="flex items-center space-x-1 sm:space-x-2">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"></path>
                </svg>
                <span className="text-xs sm:text-sm text-gray-600">SSL Encrypted</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* Product Showcase */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">🚀</span>
                </div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Unlock Your Full Potential</h1>
                <p className="text-lg text-gray-600">Get instant access to all 500+ real estate practice questions</p>
              </div>

              {/* Plan Selection */}
              <div className="space-y-4 mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Choose Your Plan:</h3>
                
                {/* Free Trial Plan */}
                <div 
                  className={`border-2 rounded-xl p-6 cursor-pointer transition-all hover:shadow-lg ${
                    plan === 'trial' ? 'border-green-600 bg-green-50' : 'border-gray-200 hover:border-green-300'
                  }`}
                  onClick={() => navigate(`/checkout?plan=trial`)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">Free Trial</h3>
                      <p className="text-gray-600">3 days free, then $99/year</p>
                      <p className="text-xs text-gray-500 mt-1">Cancel anytime during trial</p>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold text-gray-900">$0</div>
                      <div className="text-sm text-gray-600">3 days free</div>
                      <div className="text-xs text-gray-500">Then $99/year</div>
                    </div>
                  </div>
                  {plan === 'trial' && (
                    <div className="mt-3 text-center">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                        ✓ Currently Selected
                      </span>
                    </div>
                  )}
                </div>

                {/* Annual Plan */}
                <div 
                  className={`border-2 rounded-xl p-6 cursor-pointer transition-all hover:shadow-lg relative ${
                    plan === 'annual' ? 'border-purple-600 bg-purple-50' : 'border-gray-200 hover:border-purple-300'
                  }`}
                  onClick={() => navigate(`/checkout?plan=annual`)}
                >
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-4 py-1 rounded-full text-xs font-bold shadow-lg">
                      ⭐ BEST VALUE ⭐
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">Annual Plan</h3>
                      <p className="text-gray-600">Best value for long-term success</p>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold text-gray-900">$99.00</div>
                      <div className="text-sm text-gray-600">per year</div>
                    </div>
                  </div>
                  {plan === 'annual' && (
                    <div className="mt-3 text-center">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                        ✓ Currently Selected
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* State Selection */}
              <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-blue-200 mb-8">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Select Your Study State</h3>
                  <p className="text-gray-600 mb-2">Choose the state you're studying for to get personalized content</p>
                  <p className="text-sm text-orange-600 font-medium">⚠️ This selection cannot be changed later - please choose carefully</p>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
                  {[
                    { code: 'FL', name: 'Florida' },
                    { code: 'GA', name: 'Georgia' },
                    { code: 'CA', name: 'California' },
                    { code: 'TX', name: 'Texas' },
                    { code: 'NY', name: 'New York' },
                    { code: 'NJ', name: 'New Jersey' }
                  ].map((state) => (
                    <button
                      key={state.code}
                      type="button"
                      onClick={() => setFormData({ ...formData, studyState: state.code })}
                      className={`p-4 rounded-lg border-2 transition-all hover:shadow-md ${
                        formData.studyState === state.code
                          ? 'border-blue-600 bg-blue-50 text-blue-900'
                          : 'border-gray-200 hover:border-blue-300 text-gray-700'
                      }`}
                    >
                      <div className="font-bold text-lg mb-1">{state.code}</div>
                      <div className="text-xs text-gray-500">{state.name}</div>
                    </button>
                  ))}
                </div>
                
                {formData.studyState && (
                  <div className="mt-6 text-center">
                    <div className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                      <span className="mr-2">✓</span>
                      Studying for {formData.studyState} Real Estate License
                    </div>
                  </div>
                )}
              </div>

              {/* Features */}
              <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 border border-gray-100">
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">What's Included</h3>
                  <p className="text-gray-600">Everything you need to ace your real estate exam</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedPlan.features.map((feature, index) => (
                    <div key={index} className="flex items-start group">
                      <div className="flex-shrink-0 w-5 h-5 bg-green-100 rounded-full flex items-center justify-center mr-4 mt-0.5 group-hover:bg-green-200 transition-colors">
                        <span className="text-green-600 text-xs font-bold">✓</span>
                      </div>
                      <span className="text-gray-700 text-sm leading-relaxed group-hover:text-gray-900 transition-colors">{feature}</span>
                    </div>
                  ))}
                </div>
                
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-4">
                      <span className="font-semibold text-gray-900">{selectedPlan.features.length} features</span> included with your {selectedPlan.name.toLowerCase()}
                    </p>
                    {selectedPlan.savings && (
                      <div className="inline-flex items-center px-4 py-2 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
                        <span className="mr-2">💰</span>
                        {selectedPlan.savings}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Checkout Form */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {isSignup ? 'Create Account & Complete Order' : 'Payment Information'}
              </h2>

              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Signup Fields - Only show if user is not logged in */}
                {isSignup && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                          Full Name
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
                          placeholder="John Doe"
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                          Email Address
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
                          placeholder="john@example.com"
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                          Password
                        </label>
                        <input
                          type="password"
                          id="password"
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
                          placeholder="Create a password"
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                          Confirm Password
                        </label>
                        <input
                          type="password"
                          id="confirmPassword"
                          name="confirmPassword"
                          value={formData.confirmPassword}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
                          placeholder="Confirm your password"
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="border-t pt-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Information</h3>
                    </div>
                  </>
                )}
                {/* Trial Information */}
                {selectedPlan.period === 'trial' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-blue-900">Free Trial Terms</h4>
                        <div className="mt-2 text-sm text-blue-800">
                          <p>• Your 3-day free trial starts immediately</p>
                          <p>• No charge during the trial period</p>
                          <p>• After 3 days, you'll be automatically charged $99/year</p>
                          <p>• Cancel anytime during the trial to avoid charges</p>
                          <p>• You can cancel your subscription at any time</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Stripe Card Element */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Card Details
                  </label>
                  <div className="border border-gray-300 rounded-lg p-3 bg-white">
                    <CardElement
                      options={{
                        style: {
                          base: {
                            fontSize: '16px',
                            color: '#424770',
                            '::placeholder': {
                              color: '#aab7c4',
                            },
                          },
                          invalid: {
                            color: '#9e2146',
                          },
                        },
                      }}
                    />
                  </div>
                </div>

                {/* Billing Address - optional for trial to reduce friction */}
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-gray-900">Billing Address {selectedPlan.period === 'trial' && <span className="text-sm font-normal text-gray-500">(optional for trial)</span>}</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="addressLine1" className="block text-sm font-medium text-gray-700 mb-2">
                        Street Address
                      </label>
                      <input
                        type="text"
                        id="addressLine1"
                        name="addressLine1"
                        value={formData.addressLine1 || ''}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
                        placeholder="123 Main St"
                        required={selectedPlan.period !== 'trial'}
                      />
                    </div>
                    <div>
                      <label htmlFor="addressLine2" className="block text-sm font-medium text-gray-700 mb-2">
                        Apt, Suite, etc. (optional)
                      </label>
                      <input
                        type="text"
                        id="addressLine2"
                        name="addressLine2"
                        value={formData.addressLine2 || ''}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
                        placeholder="Apt 4B"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                        City
                      </label>
                      <input
                        type="text"
                        id="city"
                        name="city"
                        value={formData.city || ''}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
                        placeholder="New York"
                        required={selectedPlan.period !== 'trial'}
                      />
                    </div>
                    <div>
                      <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-2">
                        State
                      </label>
                      <input
                        type="text"
                        id="state"
                        name="state"
                        value={formData.state || ''}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
                        placeholder="NY"
                        required={selectedPlan.period !== 'trial'}
                      />
                    </div>
                    <div>
                      <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 mb-2">
                        ZIP Code
                      </label>
                      <input
                        type="text"
                        id="zipCode"
                        name="zipCode"
                        value={formData.zipCode || ''}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
                        placeholder="10001"
                        required={selectedPlan.period !== 'trial'}
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
                      Country
                    </label>
                    <select
                      id="country"
                      name="country"
                      value={formData.country || 'US'}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
                      required={selectedPlan.period !== 'trial'}
                    >
                      <option value="US">United States</option>
                      <option value="CA">Canada</option>
                      <option value="GB">United Kingdom</option>
                      <option value="AU">Australia</option>
                      <option value="DE">Germany</option>
                      <option value="FR">France</option>
                      <option value="JP">Japan</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>



                {/* Order Summary */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">{selectedPlan.name}</span>
                      <span className="font-semibold">${selectedPlan.price}</span>
                    </div>
                    {selectedPlan.savings && (
                      <div className="flex justify-between text-green-600">
                        <span>Discount</span>
                        <span>{selectedPlan.savings}</span>
                      </div>
                    )}
                    <div className="border-t pt-3">
                      <div className="flex justify-between text-lg font-bold">
                        <span>Total</span>
                        <span>${selectedPlan.price}</span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        {(() => {
                          if (selectedPlan.period === 'trial') return '3-day free trial';
                          if (selectedPlan.period === 'year') return 'Billed annually';
                          return 'Billed annually';
                        })()}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Terms and Conditions */}
                <div className="mb-6">
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={acceptedTerms}
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                      className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      required
                    />
                    <div className="text-sm text-gray-600">
                      By checking this box, you agree to the{' '}
                      <button
                        type="button"
                        onClick={() => setShowTermsModal(true)}
                        className="text-blue-600 hover:text-blue-800 underline font-medium"
                      >
                        Terms and Conditions & Disclaimer
                      </button>
                    </div>
                  </label>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading || !acceptedTerms}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-4 px-6 rounded-lg font-bold text-lg hover:shadow-lg transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-2"></div>
                      Processing...
                    </div>
                  ) : (
                    selectedPlan.period === 'trial' 
                      ? (isSignup ? 'Create Account & Start Free Trial' : 'Start Free Trial')
                      : (isSignup ? `Create Account & Pay $${selectedPlan.price}` : `Pay $${selectedPlan.price}`)
                  )}
                </button>

                {/* Trust Indicators */}
                <div className="text-center">
                  <p className="text-sm text-gray-500">
                    🔒 Secure payment • {selectedPlan.period === 'trial' ? '3-day free trial • ' : ''}Cancel anytime
                  </p>
                </div>
              </form>
            </div>

            {/* Alternative Actions */}
            <div className="text-center space-y-3">
              <button
                onClick={() => navigate('/')}
                className="text-gray-600 hover:text-gray-800 font-medium transition-colors"
              >
                ← Back to Home
              </button>
              <div className="text-sm text-gray-500">
                Need help? Contact support at support@realestart.io
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Terms and Conditions Modal - rendered in document.body so it appears above Stripe */}
      {showTermsModal && typeof document !== 'undefined' && document.body && ReactDOM.createPortal(
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setShowTermsModal(false); }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="terms-modal-title"
        >
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 sm:p-6 shrink-0 bg-white border-b flex justify-between items-center">
              <h2 id="terms-modal-title" className="text-lg sm:text-xl font-bold text-gray-900">Terms and Conditions & Disclaimer</h2>
              <button
                type="button"
                onClick={() => setShowTermsModal(false)}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto p-6 sm:p-8 text-sm text-gray-700 leading-relaxed">
              <p className="text-gray-600 mb-2">Last updated: August 31, 2025. Scroll to read the full Terms and Disclaimer.</p>

              <h3 className="text-base font-bold text-gray-900 mt-8 mb-3">Terms and Conditions</h3>
              <p className="mb-4">Please read these Terms and Conditions (&quot;Terms&quot;) carefully before using the RealEstart.io website (the &quot;Service&quot;) operated by Signal Cloud LLC (&quot;us&quot;, &quot;we&quot;, or &quot;our&quot;).</p>
              <p className="mb-4">Your access to and use of the Service is conditioned upon your acceptance of and compliance with these Terms. These Terms apply to all visitors, users, and others who wish to access or use the Service.</p>
              <p className="mb-6">By accessing or using the Service you agree to be bound by these Terms. If you disagree with any part of the terms then you do not have permission to access the Service.</p>

              <h4 className="font-semibold text-gray-900 mt-4 mb-2">1. Accounts</h4>
              <p className="mb-2">When you create an account with us, you guarantee that the information you provide is accurate, complete, and current at all times. Inaccurate, incomplete, or obsolete information may result in the immediate termination of your account on the Service.</p>
              <p className="mb-4">You are responsible for safeguarding the password that you use to access the Service and for any activities or actions under your password. You agree not to disclose your password to any third party. You must notify us immediately upon becoming aware of any breach of security or unauthorized use of your account.</p>

              <h4 className="font-semibold text-gray-900 mt-4 mb-2">2. Billing and Subscriptions</h4>
              <p className="mb-2">The Service is billed on a subscription basis. You will be billed in advance on a recurring and periodic basis (e.g., annually), depending on the subscription plan you select.</p>
              <p className="mb-2">At the end of each period, your Subscription will automatically renew unless you cancel it or Signal Cloud LLC cancels it. You may cancel through your online account management page or by contacting customer support. Upon cancellation, you will not be charged for future billing cycles but will retain access for the remainder of your current paid period.</p>
              <p className="mb-4">A valid payment method is required. You shall provide accurate and complete billing information. By submitting payment information, you authorize Signal Cloud LLC to charge all Subscription fees to such payment instruments.</p>

              <h4 className="font-semibold text-gray-900 mt-4 mb-2">3. Refunds</h4>
              <p className="mb-4">All Subscription fees are non-refundable. We will not provide refunds or credits for any partial subscription periods. We reserve the right, at our sole discretion, to offer a refund or credit in unusual or exceptional circumstances.</p>

              <h4 className="font-semibold text-gray-900 mt-4 mb-2">4. Intellectual Property</h4>
              <p className="mb-4">The Service and its original content, features, and functionality are and will remain the exclusive property of Signal Cloud LLC and its licensors. The Service is protected by copyright, trademark, and other laws. Our trademarks may not be used without prior written consent.</p>

              <h4 className="font-semibold text-gray-900 mt-4 mb-2">5. Prohibited Uses</h4>
              <p className="mb-2">You may use the Service only for lawful purposes. You agree not to use the Service: in any way that violates applicable law; to exploit or harm minors; to transmit spam or similar solicitation; to impersonate us or others; or to engage in conduct that restricts others&apos; use or harms Signal Cloud LLC or users.</p>

              <h4 className="font-semibold text-gray-900 mt-4 mb-2">6. Termination</h4>
              <p className="mb-4">We may terminate or suspend your account and bar access to the Service immediately, without prior notice or liability, for any reason including breach of the Terms.</p>

              <h4 className="font-semibold text-gray-900 mt-4 mb-2">7. Governing Law</h4>
              <p className="mb-4">These Terms shall be governed by the laws of Georgia, United States, without regard to conflict of law provisions.</p>

              <h4 className="font-semibold text-gray-900 mt-4 mb-2">8. Changes to Terms</h4>
              <p className="mb-4">We reserve the right to modify or replace these Terms at any time. By continuing to use the Service after revisions become effective, you agree to be bound by the revised terms.</p>

              <h4 className="font-semibold text-gray-900 mt-4 mb-2">9. Contact</h4>
              <p className="mb-8">If you have any questions about these Terms, please contact us.</p>

              <h3 className="text-base font-bold text-gray-900 mt-8 mb-3">Disclaimer</h3>
              <p className="mb-4">This Disclaimer governs your use of the RealEstart.io website and all associated services, including flashcards, practice tests, and study materials (collectively, the &quot;Services&quot;). RealEstart.io is owned and operated by Signal Cloud LLC.</p>

              <h4 className="font-semibold text-gray-900 mt-4 mb-2">1. No Guarantee of Results & Study Aid Only</h4>
              <p className="mb-4">RealEstart.io is a supplementary study aid and educational tool only. We do not guarantee that your use of our Services will result in passing any real estate licensing examination. The content is for informational and practice purposes only. Your success depends on your individual effort, preparation, and understanding.</p>

              <h4 className="font-semibold text-gray-900 mt-4 mb-2">2. Not a Licensed Educational Provider</h4>
              <p className="mb-4">RealEstart.io and Signal Cloud LLC are not a licensed or accredited real estate school or provider of official pre-licensing courses. In Georgia, you must complete a 75-hour pre-licensing course from a licensed school to be eligible for the exam. Our Services are not a substitute for this requirement.</p>

              <h4 className="font-semibold text-gray-900 mt-4 mb-2">3. No Use of Actual Exam Questions</h4>
              <p className="mb-4">Content in the Services, including practice questions and flashcards, is the original intellectual property of Signal Cloud LLC. None of the content is derived from or intended to replicate any official licensing examination. Our materials test general real estate principles. Any resemblance to actual exam questions is coincidental.</p>

              <h4 className="font-semibold text-gray-900 mt-4 mb-2">4. Assumption of Risk</h4>
              <p className="mb-4">By using RealEstart.io, you acknowledge that you do so at your own risk. You assume all responsibility for any damages or losses arising from use of our Services, including financial loss or failure to pass an exam.</p>

              <h4 className="font-semibold text-gray-900 mt-4 mb-2">5. Limitation of Liability and Indemnification</h4>
              <p className="mb-2">To the fullest extent permitted by law, Signal Cloud LLC and its affiliates shall not be liable for any direct, indirect, incidental, special, consequential, or punitive damages resulting from: your access to or use of the Services; any third-party conduct or content; content obtained from the Services; or unauthorized access or alteration of your data.</p>
              <p className="mb-2">You agree to defend, indemnify, and hold harmless Signal Cloud LLC from any claims, damages, or expenses arising from: your use of the Services; your violation of these documents; your violation of any third-party right; or any claim that your use caused damage to a third party. This obligation survives termination of your use.</p>

              <h4 className="font-semibold text-gray-900 mt-4 mb-2">6. Not Professional or Legal Advice</h4>
              <p className="mb-6">The content on RealEstart.io is for informational purposes only and is not professional or legal advice. Consult a qualified real estate professional or attorney for advice tailored to your situation.</p>

              <p className="text-gray-900 font-medium pt-4 border-t">
                By checking the box and completing checkout, you confirm that you have read and agree to these Terms and Conditions and Disclaimer.
              </p>
            </div>
          </div>
        </div>,
        document.body
      )}
      </div>
    );
  };

// Wrapper component to provide Stripe Elements context
const Checkout: React.FC = () => {
  return (
    <Elements stripe={stripePromise}>
      <CheckoutForm />
    </Elements>
  );
};

export default Checkout;